import { Skeletton } from "@/atoms-react/skeletton/index.react";
import { Base, Info } from "@/atoms-react/text/Text.react";
import { Routes } from "@/features/router";
import { useDocument } from "@/features/user/document/hooks/use-document";
import { useUser } from "@/features/user/hooks/use-user";
import { DocumentRow } from "@/molecules/document-row/index.react";
import { ModalKYCIdentity } from "@/molecules/modal-kyc-identity/index.react";
import { Themes } from "@/types/theme";
import _ from "lodash";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Button } from "@/atoms-react/button/Button.react.tsx";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { isIdentityValid } from "@/utils/declarative.utils";
import { identityPartners, mapNpToNpDto } from "@/features/user/types";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { usePartnerInfo } from "@/features/partner/hooks/use-partner-information";
import { ModalIbanUniqAccount } from "@/molecules/modal-uniq-scenario/index.react";
import { KYCSessionScenario } from "@/features/kyc/types";

type IdentityDocumentsProps = React.ComponentPropsWithoutRef<"div">;

export const LevelDocuments = ({ ...props }: IdentityDocumentsProps) => {
  const { t } = useTranslation(["pass"]);

  const { useUserProfile } = useUser();
  const { data: user } = useUserProfile();
  const { kycLevel } = useKYCContext();
  const origin = useOrigin();
  const { DocumentsDetailsQuery } = useDocument();
  const { data: documents, isLoading: isLoadingDocuments } = DocumentsDetailsQuery(
    user?.naturalPerson?.UUID ?? null
  );
  const { partnerUuidQuery } = usePartnerInfo();

  const { data: dataPartner } = partnerUuidQuery(
    (sessionStorage.getItem("origin") as string) ?? null
  );

  const navigate = useNavigate();
  const [openKyc, setOpenKyc] = useState<boolean>(false);
  const isVerificationButtonDisabled =
    user?.naturalPerson === undefined ? true : !isIdentityValid(mapNpToNpDto(user?.naturalPerson));

  const identityPartner = (() =>
    user?.identityPartners?.find(
      (partner: identityPartners) => partner.uuid === dataPartner?.data.partner_uuid
    ))();
  return (
    <div
      className={twMerge(
        "flex flex-col justify-between md:ml-6 md:block md:pt-4 md:pb-6 2xl:ml-0",
        props.className
      )}
      {..._.omit(props, "className")}
    >
      {origin === FromURL.Partner &&
      identityPartner?.custom_scenario === KYCSessionScenario.Fusion ? (
        <ModalIbanUniqAccount isOpen={openKyc} onClose={() => setOpenKyc(false)} />
      ) : (
        <ModalKYCIdentity
          open={openKyc}
          onClose={() => {
            sessionStorage.removeItem("DocumentKsyc");
            setOpenKyc(false);
          }}
        />
      )}

      <div>
        <div className="mb-3 flex justify-between md:px-6">
          <Base className="hidden text-slate-300 md:block">
            {t("olkypass.accountPage.basicInfo.tabs.documents")}
          </Base>
          {(kycLevel as number) < 2 && (
            <Button
              dataCy="start-kyc"
              className="w-max shrink-0"
              size="md"
              onClick={() => setOpenKyc(true)}
              theme={Themes.primary}
              disabled={isVerificationButtonDisabled}
            >
              {t("olkypass.general.open_kyc")}
            </Button>
          )}
        </div>
        <div className={"mb-4"}>
          {!documents?.length && (
            <Info className="px-6">
              {t("olkypass.accountPage.documentsSection.help.no_data_found")}
            </Info>
          )}
          {documents &&
            documents.slice(0, 2).map((document) => (
              <DocumentRow
                key={document.uuid}
                onClick={() =>
                  navigate({
                    pathname: Routes.account_alias_documents,
                    search: `?preview=${document.uuid}`
                  })
                }
                document={document}
              />
            ))}
          {isLoadingDocuments &&
            [0, 1, 2].map((val) => <Skeletton key={val} className={"mx-6 mb-1"} />)}
        </div>
      </div>

      {/*<AddDocument />*/}
    </div>
  );
};
